import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LayoutDashboard, Users, MessageCircle, CalendarDays, User } from 'lucide-react';
import { motion } from 'framer-motion';
import { getMatches } from '../lib/api';

const BottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [unreadTotal, setUnreadTotal] = useState(0);

  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const res = await getMatches();
        const total = (res.data || []).reduce((sum: number, m: any) => sum + (m.unread_count || 0), 0);
        setUnreadTotal(total);
      } catch {}
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => clearInterval(interval);
  }, []);

  const tabs = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/matches', icon: Users, label: 'Matches' },
    { path: '/chats', icon: MessageCircle, label: 'Chats', badge: unreadTotal },
    { path: '/date-plan', icon: CalendarDays, label: t('nav.dates') },
    { path: '/profile', icon: User, label: t('nav.profile') },
  ];

  return (
    <nav data-testid="bottom-nav" className="fixed bottom-0 left-0 right-0 max-w-md mx-auto bg-white border-t border-neutral-100 h-16 flex justify-around items-center z-50 padding-safe shadow-[0_-4px_20px_rgba(0,0,0,0.03)]">
      {tabs.map((tab) => {
        const isActive = location.pathname === tab.path ||
          (tab.path === '/dashboard' && location.pathname === '/discover') ||
          (tab.path === '/chats' && location.pathname.startsWith('/chat/'));
        const Icon = tab.icon;
        return (
          <button
            key={tab.path}
            data-testid={`nav-${tab.label.toLowerCase()}`}
            onClick={() => navigate(tab.path)}
            className="relative flex flex-col items-center gap-0.5 px-3 py-1 rounded-xl transition-all duration-200"
            aria-label={tab.label}
          >
            {isActive && (
              <motion.div
                layoutId="nav-indicator"
                className="absolute -top-0.5 w-6 h-0.5 bg-dutch-teal rounded-full"
                transition={{ type: 'spring', stiffness: 400, damping: 30 }}
              />
            )}
            <div className="relative">
              <motion.div
                animate={{ scale: isActive ? 1 : 0.9, y: isActive ? -1 : 0 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              >
                <Icon
                  size={22}
                  strokeWidth={isActive ? 2.5 : 1.8}
                  className={`transition-colors duration-200 ${isActive ? 'text-dutch-teal' : 'text-neutral-400'}`}
                />
              </motion.div>
              {(tab.badge ?? 0) > 0 && (
                <span className="absolute -top-1.5 -right-2 min-w-[16px] h-4 bg-dutch-teal text-white text-[9px] font-bold rounded-full flex items-center justify-center px-1">
                  {(tab.badge ?? 0) > 9 ? '9+' : tab.badge}
                </span>
              )}
            </div>
            <span className={`text-[10px] font-semibold tracking-wide transition-colors duration-200 ${
              isActive ? 'text-dutch-teal' : 'text-neutral-400'
            }`}>
              {tab.label}
            </span>
          </button>
        );
      })}
    </nav>
  );
};

export default BottomNav;
